import { gql, useQuery } from "@apollo/client";
import {
  Link,
  makeStyles,
  TextField,
  TextFieldProps,
  Typography,
} from "@material-ui/core";
import AutoComplete from "@material-ui/lab/Autocomplete";
import cx from "clsx";
import { useFlashMessage } from "components/legacy/FlashMessage/FlashMessage";
import IntegrationModal from "components/legacy/Settings/Integrations/IntegrationModal";
import {
  GreenhouseAPIData,
  GreenhouseAPIData_atsUsers as GreenhouseUser,
  UpdateGreenhouseAPI,
  UpdateGreenhouseAPIVariables,
} from "generated/schemaTypes";
import greenhouseIconLg from "icons/greenhouse-icon-lg.svg";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { analytics } from "react-hooks/useAnalytics";
import { captureSentryError } from "utils/errors/sentryError";
import useMutation from "utils/useMutation";

const useStyles = makeStyles({
  root: {},
  inlineFormField: {
    display: "flex",
    alignItems: "center",
  },
  description: {
    marginBottom: "1.5rem",
  },
  subdomain: {
    width: 300,
    paddingRight: 4,
    paddingLeft: 4,
  },
  subdomainHelpers: {
    paddingTop: 20,
  },
  formField: {
    marginTop: "2rem",
  },
  syncContainer: {
    marginTop: "2rem",
    "& $formField": {
      marginTop: "1rem",
    },
  },
});

const GREENHOUSE_API_DATA = gql`
  query GreenhouseAPIData {
    atsUsers(
      orderBy: [{ name: asc }]
      where: { disabled: false, isRealUser: true }
    ) {
      id
      name
    }
    currentUserPrisma {
      id
      currentOrganization {
        id
        customer {
          id
          greenhouseIntegration {
            id
            connected
            webhooksSecretKey
            apiKeySuffix
            maildropAddress
            subdomain
            boardToken
            onBehalfOfUserId
          }
        }
      }
    }
  }
`;

const UPDATE_GREENHOUSE_API_DATA = gql`
  mutation UpdateGreenhouseAPI($input: UpdateCustomerGreenhouseInput!) {
    updateCustomerGreenhouse(input: $input) {
      success
      message
      code
      customer {
        id
        greenhouseIntegration {
          id
          connected
          apiKeySuffix
          maildropAddress
          subdomain
          requiredWebhooksConfigured
          onBehalfOfUserId
        }
      }
    }
  }
`;

type GreenhouseApiModalProps = {
  open: boolean;
  onConnected?: () => void;
  onClose: () => void;
};

function GreenhouseApiModal({
  open,
  onConnected,
  onClose,
}: GreenhouseApiModalProps) {
  const styles = useStyles();
  const { data, loading } = useQuery<GreenhouseAPIData>(GREENHOUSE_API_DATA);
  const greenhouseIntegration =
    data?.currentUserPrisma?.currentOrganization?.customer
      .greenhouseIntegration;
  const availableOnBehalfOfUsers = useMemo(() => data?.atsUsers ?? [], [data]);
  const apiKeySuffix = greenhouseIntegration?.apiKeySuffix;

  const [apiKey, setApiKey] = useState("");
  const [subdomain, setSubdomain] = useState("app");
  const [boardToken, setBoardToken] = useState("");
  const [maildropAddress, setMaildropAddress] = useState(
    "maildrop@rose.greenhouse.io"
  );
  const [onBehalfOfUser, setOnBehalfOfUser] = useState<GreenhouseUser>();
  const [update, { loading: updateSaving }] = useMutation<
    UpdateGreenhouseAPI,
    UpdateGreenhouseAPIVariables
  >(UPDATE_GREENHOUSE_API_DATA);

  useEffect(() => {
    if (greenhouseIntegration?.connected) {
      setMaildropAddress(greenhouseIntegration.maildropAddress);
      setSubdomain(greenhouseIntegration.subdomain);
      setBoardToken(greenhouseIntegration.boardToken || "");
      if (greenhouseIntegration.onBehalfOfUserId) {
        setOnBehalfOfUser(
          _.find(availableOnBehalfOfUsers, {
            id: greenhouseIntegration.onBehalfOfUserId,
          })
        );
      }
    }
  }, [greenhouseIntegration, availableOnBehalfOfUsers]);

  const { setContent } = useFlashMessage();

  const handleUpdate = useCallback(async () => {
    try {
      await update({
        variables: {
          input: {
            ...(apiKey ? { apiKey } : {}),
            ...(onBehalfOfUser ? { onBehalfOfUserId: onBehalfOfUser.id } : {}),
            ...{ subdomain },
            ...{ boardToken },
            maildropAddress,
          },
        },
        refetchQueries: [
          "GetGreenhouseAPIData",
          "FetchAllCustomerIntegrationsPrisma",
        ],
        awaitRefetchQueries: true,
      });

      // The API Key has changed (and is currently being set, but not to a fake key by our sales team)
      if (apiKey && apiKey.slice(-4) !== apiKeySuffix && apiKey !== "fake") {
        analytics.track("Greenhouse API Connected");
      }

      // Clear the apiKey in the form after save, it will get replaced with the
      // apiKeySuffix from the server
      setApiKey("");
    } catch (error) {
      setContent({
        content: "Something went wrong updating your Greenhouse integration",
        severity: "error",
      });
      captureSentryError(error, "GreenhouseApiModal handleUpdate");
      return;
    }
    if (onConnected) onConnected();
    onClose();
  }, [
    onConnected,
    onClose,
    update,
    apiKey,
    onBehalfOfUser,
    subdomain,
    boardToken,
    maildropAddress,
    apiKeySuffix,
    setContent,
  ]);

  const disabled =
    !maildropAddress ||
    (!apiKey && !apiKeySuffix) ||
    (!onBehalfOfUser && greenhouseIntegration?.connected);
  const placeholder = apiKeySuffix
    ? `xxxxxxxxxxxxxxxxxxxx${apiKeySuffix}`
    : "ie: wrKa9jw3wuw90AZ2xpwiQ4BGu";

  return (
    <IntegrationModal
      open={open}
      onClose={onClose}
      title="Greenhouse"
      logoSrc={greenhouseIconLg.src}
      onSave={handleUpdate}
      disabled={disabled}
      loading={loading}
      updateSaving={updateSaving}
    >
      <Typography className={styles.description} variant="body1">
        The Guide Greenhouse integration allows you to automatically add
        schedules, interviewers and stages to your Guide.
      </Typography>
      <Typography className={styles.description} variant="body1">
        See our{" "}
        <Link
          href="https://intercom.help/guidedotco/en/articles/4543311-integrating-with-greenhouse"
          target="_blank"
        >
          Setup Instructions
        </Link>{" "}
        for help creating a Harvest API key, including the list of permissions
        we require.
      </Typography>
      <TextField
        className={styles.formField}
        variant="outlined"
        label="Harvest API Key"
        InputLabelProps={{
          shrink: true,
        }}
        onChange={(e) => setApiKey(e.target.value)}
        placeholder={placeholder}
        fullWidth
        value={apiKey}
      />
      <div className={styles.inlineFormField}>
        <Typography variant="subtitle2" className={styles.subdomainHelpers}>
          https://
        </Typography>
        <TextField
          className={cx(styles.formField, styles.subdomain)}
          variant="outlined"
          label="Greenhouse (SSO) Subdomain"
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(e): void => setSubdomain(e.target.value)}
          placeholder="e.g. yourcompany"
          fullWidth
          value={subdomain}
        />
        <Typography variant="subtitle2" className={styles.subdomainHelpers}>
          .greenhouse.io
        </Typography>
      </div>
      <div className={styles.inlineFormField}>
        <Typography variant="subtitle2" className={styles.subdomainHelpers}>
          https://boards.greenhouse.io/
        </Typography>
        <TextField
          className={cx(styles.formField, styles.subdomain)}
          variant="outlined"
          label="Greenhouse Job Board Token"
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(e): void => setBoardToken(e.target.value)}
          placeholder="e.g. yourcompany"
          fullWidth
          value={boardToken}
        />
      </div>
      <AutoComplete
        className={styles.formField}
        options={[
          "maildrop@rose.greenhouse.io",
          "maildrop@fern.greenhouse.io",
          "maildrop@ivy.greenhouse.io",
          "maildrop@lily.greenhouse.io",
          "maildrop@iris.greenhouse.io",
        ]}
        value={maildropAddress}
        onChange={(_e: React.ChangeEvent<unknown>, value: string | null) => {
          if (value) {
            setMaildropAddress(value);
          }
        }}
        renderInput={(params: TextFieldProps) => (
          <TextField
            {...params}
            label="Greenhouse Maildrop Address"
            placeholder="eg. maildrop@rose.greenhouse.io"
            variant="outlined"
            fullWidth
          />
        )}
      />
      {greenhouseIntegration?.connected && (
        <div className={styles.syncContainer}>
          <Typography variant="subtitle1">
            All requests to Greenhouse must be attributed to a user in
            Greenhouse. Select a Default Sync User from within your company or
            create a new user in Greenhouse specifically for this purpose.
          </Typography>
          {availableOnBehalfOfUsers.length && (
            <AutoComplete<GreenhouseUser, false, true, false>
              className={styles.formField}
              options={availableOnBehalfOfUsers}
              disableClearable
              getOptionLabel={(option) => option?.name || ""}
              value={onBehalfOfUser}
              onChange={(
                _e: React.ChangeEvent<unknown>,
                value: GreenhouseUser
              ) => {
                if (value) {
                  setOnBehalfOfUser(value);
                }
              }}
              renderInput={(params: TextFieldProps) => (
                <TextField
                  {...params}
                  label="Sync User"
                  placeholder="john@example.com"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          )}
        </div>
      )}
    </IntegrationModal>
  );
}

export default GreenhouseApiModal;
